import type {StateCreator} from 'zustand'
import type {IssueComment} from '../../page-data/payloads/issue-comments'

export type IssueCommentSlice = {
  issueComments: IssueComment[]
  // This is separate from issueComments.length because we don't load that data until the user opens the comments panel,
  // but we want to show the count in the UI before that happens
  issueCommentsCount: number

  commentsPanelActions: {
    setIssueComments: (comments: IssueComment[]) => void
    addIssueComment: (comment: IssueComment) => void
    updateIssueComment: (args: {
      match: (comment: IssueComment) => boolean
      transform: (comment: IssueComment | undefined) => IssueComment | undefined
    }) => void
    incrementIssueCommentsCount: () => void
  }
}

export type IssueCommentsSliceOpts = {
  issueCommentsCount: number
}

export const createIssueCommentSlice =
  ({
    issueCommentsCount,
  }: IssueCommentsSliceOpts): StateCreator<IssueCommentSlice, [['zustand/devtools', never]], [], IssueCommentSlice> =>
  (set, get) => ({
    issueComments: [],
    issueCommentsCount,

    commentsPanelActions: {
      setIssueComments: comments => {
        set({issueComments: comments}, undefined, 'setIssueComments')
      },

      addIssueComment: comment => {
        set(
          state => {
            const updatedIssueCommentsArray = [comment, ...state.issueComments]
            return {
              issueComments: updatedIssueCommentsArray,
            }
          },
          undefined,
          'addIssueComment',
        )
        get().commentsPanelActions.incrementIssueCommentsCount()
      },

      updateIssueComment: ({match, transform}) => {
        set(
          state => ({
            issueComments: state.issueComments.map(comment => {
              if (match(comment)) {
                const updated = transform(comment)
                return updated ?? comment
              }
              return comment
            }),
          }),
          undefined,
          'updateIssueComment',
        )
      },

      incrementIssueCommentsCount: () => {
        set(
          state => ({
            issueCommentsCount: state.issueCommentsCount + 1,
          }),
          undefined,
          'incrementIssueCommentsCount',
        )
      },
    },
  })
